import { useRef, useState } from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_MS = 300;

function distance(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

// Standalone/installed PWAs don't get the browser's native page pinch-zoom,
// so this reimplements pinch + double-tap zoom with raw pointer events.
export function ZoomableImage({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isPinching, setIsPinching] = useState(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchStart = useRef<{ distance: number; scale: number } | null>(null);
  const panStart = useRef<{
    x: number;
    y: number;
    tx: number;
    ty: number;
  } | null>(null);
  const lastTap = useRef(0);

  function handlePointerDown(e: React.PointerEvent) {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values()) as [
        { x: number; y: number },
        { x: number; y: number },
      ];
      pinchStart.current = { distance: distance(a, b), scale };
      panStart.current = null;
      setIsPinching(true);
    } else if (pointers.current.size === 1) {
      if (scale > 1) {
        panStart.current = {
          x: e.clientX,
          y: e.clientY,
          tx: translate.x,
          ty: translate.y,
        };
      }
      const now = Date.now();
      if (now - lastTap.current < DOUBLE_TAP_MS) {
        setScale(scale > 1 ? 1 : 2.5);
        setTranslate({ x: 0, y: 0 });
      }
      lastTap.current = now;
    }
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2 && pinchStart.current) {
      const [a, b] = Array.from(pointers.current.values()) as [
        { x: number; y: number },
        { x: number; y: number },
      ];
      const ratio = distance(a, b) / pinchStart.current.distance;
      setScale(
        Math.min(
          MAX_SCALE,
          Math.max(MIN_SCALE, pinchStart.current.scale * ratio),
        ),
      );
    } else if (pointers.current.size === 1 && panStart.current) {
      setTranslate({
        x: panStart.current.tx + (e.clientX - panStart.current.x),
        y: panStart.current.ty + (e.clientY - panStart.current.y),
      });
    }
  }

  function handlePointerUp(e: React.PointerEvent) {
    pointers.current.delete(e.pointerId);
    pinchStart.current = null;
    panStart.current = null;
    setIsPinching(false);
    if (pointers.current.size === 0 && scale <= 1) {
      setTranslate({ x: 0, y: 0 });
    }
  }

  return (
    <div
      className="touch-none overflow-hidden"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onDoubleClick={() =>
        scale > 1 ? (setScale(1), setTranslate({ x: 0, y: 0 })) : setScale(2.5)
      }
    >
      <img
        src={src}
        alt={alt}
        className={className}
        draggable={false}
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transition: isPinching ? "none" : "transform 0.15s ease-out",
          cursor: scale > 1 ? "grab" : "default",
          touchAction: "none",
        }}
      />
    </div>
  );
}
